import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  CircularProgress,
  Collapse,
  Switch,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import KeyboardVoiceIcon from '@mui/icons-material/KeyboardVoice';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import SendIcon from '@mui/icons-material/Send';
import CodeIcon from '@mui/icons-material/Code';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import { ranchAssistantAPI } from '../services/api';
import { cardSx } from '../theme';

const SPEECH_IDLE = 'idle';
const SPEECH_LISTENING = 'listening';
const SPEECH_PROCESSING = 'processing';
const SPEECH_ERROR = 'error';
const AUTO_SUBMIT_DELAY_MS = 1200;

function RanchAssistantPage() {
  const recognitionRef = useRef(null);
  const autoSubmitTimerRef = useRef(null);
  const latestTranscriptRef = useRef('');
  const latestQuestionDraftRef = useRef('');
  const pendingAutoSubmitRef = useRef(false);
  const hasRecognitionErrorRef = useRef(false);
  const performSubmitRef = useRef(null);
  const loadingRef = useRef(false);
  const audioRef = useRef(null);
  const currentAudioUrlRef = useRef(null);
  const selectedVoiceIdRef = useRef('');
  const selectedVoiceNameRef = useRef('');
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [sql, setSql] = useState('');
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSql, setShowSql] = useState(false);
  const [speechStatus, setSpeechStatus] = useState(SPEECH_IDLE);
  const [speechError, setSpeechError] = useState('');
  const [autoSubmitPending, setAutoSubmitPending] = useState(false);
  const [ttsSupported, setTtsSupported] = useState(true);
  const [ttsEnabled, setTtsEnabled] = useState(true);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState('');
  const [voicesLoaded, setVoicesLoaded] = useState(false);
  const [ttsNotice, setTtsNotice] = useState('');
  const [lastRequestUsedVoice, setLastRequestUsedVoice] = useState(false);
  const [showAnswerText, setShowAnswerText] = useState(true);

  const extractTtsErrorMessage = async (err) => {
    const fallback = 'Unable to generate ElevenLabs speech.';
    if (!err) return fallback;

    try {
      const responseData = err.response?.data;
      if (!responseData) return err.message || fallback;

      if (typeof responseData === 'string') {
        const parsed = JSON.parse(responseData);
        return parsed?.error || responseData || fallback;
      }

      if (responseData instanceof Blob) {
        const text = await responseData.text();
        if (!text) return fallback;
        try {
          const parsed = JSON.parse(text);
          return parsed?.error || text || fallback;
        } catch (_jsonErr) {
          return text || fallback;
        }
      }

      if (typeof responseData === 'object') {
        return responseData.error || responseData.message || fallback;
      }
    } catch (_err) {
      // Ignore and fall through.
    }

    return err.message || fallback;
  };

  const speechSupported = useMemo(() => {
    return Boolean(window.SpeechRecognition || window.webkitSpeechRecognition);
  }, []);

  useEffect(() => {
    audioRef.current = new Audio();
    audioRef.current.onplay = () => setIsSpeaking(true);
    audioRef.current.onended = () => setIsSpeaking(false);
    audioRef.current.onpause = () => setIsSpeaking(false);
    audioRef.current.onerror = () => {
      setIsSpeaking(false);
      setTtsNotice('Unable to play ElevenLabs audio.');
    };

    const loadVoices = async () => {
      try {
        const response = await ranchAssistantAPI.getTtsVoices();
        const voices = response.data?.voices || [];
        const defaultVoiceId = response.data?.defaultVoiceId || '';
        setVoicesLoaded(true);

        if (!voices.length) {
          setTtsSupported(false);
          setTtsNotice('No ElevenLabs voices are available.');
          return;
        }

        let chosen = null;
        if (defaultVoiceId) {
          chosen = voices.find((voice) => voice.voiceId === defaultVoiceId) || null;
        }
        if (!chosen) {
          chosen = voices[0];
        }

        selectedVoiceIdRef.current = chosen.voiceId || '';
        selectedVoiceNameRef.current = chosen.name || '';
        setSelectedVoice(selectedVoiceNameRef.current);
        setTtsNotice('');
      } catch (err) {
        const msg = err.response?.data?.error || 'ElevenLabs TTS is unavailable.';
        setTtsSupported(false);
        setVoicesLoaded(true);
        setTtsNotice(msg);
      }
    };

    loadVoices();

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      if (currentAudioUrlRef.current) {
        URL.revokeObjectURL(currentAudioUrlRef.current);
        currentAudioUrlRef.current = null;
      }
    };
  }, []);

  const speakText = async (textToSpeak) => {
    const text = (textToSpeak || '').trim();
    if (!ttsSupported || !text) return;
    if (!selectedVoiceIdRef.current) {
      setTtsNotice('No ElevenLabs voice is available for playback.');
      return;
    }

    try {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      if (currentAudioUrlRef.current) {
        URL.revokeObjectURL(currentAudioUrlRef.current);
        currentAudioUrlRef.current = null;
      }

      const response = await ranchAssistantAPI.speakTts({
        text,
        voiceId: selectedVoiceIdRef.current,
      });
      const blobUrl = URL.createObjectURL(response.data);
      currentAudioUrlRef.current = blobUrl;
      audioRef.current.src = blobUrl;
      await audioRef.current.play();
      setTtsNotice('');
    } catch (err) {
      setIsSpeaking(false);
      const message = await extractTtsErrorMessage(err);
      setTtsNotice(message);
    }
  };

  const stopSpeaking = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setIsSpeaking(false);
  };

  const clearAutoSubmitTimer = () => {
    if (autoSubmitTimerRef.current) {
      clearTimeout(autoSubmitTimerRef.current);
      autoSubmitTimerRef.current = null;
    }
    setAutoSubmitPending(false);
  };

  const stopRecognition = () => {
    if (!recognitionRef.current) return;
    try {
      recognitionRef.current.stop();
    } catch (err) {
      // Ignore stop errors when recognition is not active.
    }
  };

  const performSubmit = async (nextQuestion, options = {}) => {
    const { stopVoice = true, fromVoiceInput = false } = options;
    const trimmed = (nextQuestion ?? question).trim();
    if (!trimmed || loadingRef.current) return false;

    clearAutoSubmitTimer();
    pendingAutoSubmitRef.current = false;
    hasRecognitionErrorRef.current = false;
    latestTranscriptRef.current = '';
    latestQuestionDraftRef.current = '';

    if (stopVoice) {
      stopRecognition();
      setSpeechStatus(SPEECH_IDLE);
    }

    setLoading(true);
    loadingRef.current = true;
    setError('');
    setAnswer('');
    setSql('');
    setRowCount(0);
    setLastRequestUsedVoice(fromVoiceInput);
    setShowAnswerText(!fromVoiceInput);

    try {
      const response = await ranchAssistantAPI.ask({ question: trimmed });
      const nextAnswer = response.data?.answer || '';
      setAnswer(nextAnswer);
      setSql(response.data?.sql || '');
      setRowCount(Number(response.data?.rowCount || 0));
      if (fromVoiceInput || ttsEnabled) {
        await speakText(nextAnswer);
      }
      return true;
    } catch (err) {
      setError(err.response?.data?.error || 'Unable to reach ranch assistant right now.');
      return false;
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  };

  performSubmitRef.current = performSubmit;

  useEffect(() => {
    if (!speechSupported) return undefined;

    const RecognitionClass = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new RecognitionClass();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = 'en-US';

    recognition.onstart = () => {
      clearAutoSubmitTimer();
      pendingAutoSubmitRef.current = false;
      hasRecognitionErrorRef.current = false;
      latestTranscriptRef.current = '';
      setSpeechStatus(SPEECH_LISTENING);
      setSpeechError('');
    };

    recognition.onspeechend = () => {
      setSpeechStatus(SPEECH_PROCESSING);
    };

    recognition.onresult = (event) => {
      const transcript = event.results?.[0]?.[0]?.transcript?.trim() || '';
      if (transcript) {
        latestTranscriptRef.current = transcript;
        setQuestion((prev) => {
          const nextQuestion = transcript;
          latestQuestionDraftRef.current = nextQuestion;
          return nextQuestion;
        });
      }
    };

    recognition.onend = () => {
      setSpeechStatus((prev) => (prev === SPEECH_ERROR ? SPEECH_ERROR : SPEECH_IDLE));
      if (hasRecognitionErrorRef.current || !latestTranscriptRef.current || loadingRef.current || pendingAutoSubmitRef.current) {
        return;
      }

      pendingAutoSubmitRef.current = true;
      setAutoSubmitPending(true);
      clearAutoSubmitTimer();
      autoSubmitTimerRef.current = setTimeout(async () => {
        pendingAutoSubmitRef.current = false;
        setAutoSubmitPending(false);
        const textToSubmit = latestQuestionDraftRef.current || latestTranscriptRef.current;
        if (performSubmitRef.current) {
          await performSubmitRef.current(textToSubmit, { stopVoice: false, fromVoiceInput: true });
        }
      }, AUTO_SUBMIT_DELAY_MS);
    };

    recognition.onerror = (event) => {
      const code = event?.error || 'unknown';
      let message = 'Voice input failed. Please try again.';
      if (code === 'not-allowed') message = 'Microphone permission was denied. Enable microphone access to use voice input.';
      if (code === 'no-speech') message = 'No speech was detected. Try again and speak clearly.';
      if (code === 'network') message = 'Speech recognition network error. Check your connection and try again.';

      hasRecognitionErrorRef.current = true;
      pendingAutoSubmitRef.current = false;
      clearAutoSubmitTimer();
      setSpeechStatus(SPEECH_ERROR);
      setSpeechError(message);
    };

    recognitionRef.current = recognition;

    return () => {
      clearAutoSubmitTimer();
      stopRecognition();
      recognitionRef.current = null;
    };
  }, [speechSupported]);

  const handleStartVoice = () => {
    if (!recognitionRef.current) return;
    clearAutoSubmitTimer();
    pendingAutoSubmitRef.current = false;
    hasRecognitionErrorRef.current = false;
    stopSpeaking();
    latestTranscriptRef.current = '';
    latestQuestionDraftRef.current = '';
    setQuestion('');
    setSpeechError('');
    setSpeechStatus(SPEECH_LISTENING);
    try {
      recognitionRef.current.start();
    } catch (err) {
      setSpeechStatus(SPEECH_ERROR);
      setSpeechError('Voice input could not start. Please try again.');
    }
  };

  const handleStopVoice = () => {
    clearAutoSubmitTimer();
    pendingAutoSubmitRef.current = false;
    stopRecognition();
    setSpeechStatus(SPEECH_IDLE);
    stopSpeaking();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    await performSubmit();
  };

  return (
    <Box sx={{ maxWidth: 1000 }}>
      <Typography variant="h4" sx={{ mb: 1, fontWeight: 700 }}>
        Ranch Assistant
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Ask questions in plain language and get answers from your ranch database.
      </Typography>

      <Paper sx={{ ...cardSx, p: 3, borderRadius: 3 }}>
        <Stack component="form" spacing={2} onSubmit={handleSubmit}>
          <TextField
            size="small"
            multiline
            minRows={3}
            label="Question"
            placeholder="Example: Which mares are confirmed in foal and what are their due dates?"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            disabled={loading}
            fullWidth
          />

          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
            {speechSupported ? (
              <>
                {speechStatus === SPEECH_LISTENING ? (
                  <Button
                    variant="outlined"
                    color="warning"
                    onClick={handleStopVoice}
                    startIcon={<StopCircleIcon />}
                    disabled={loading}
                  >
                    Stop Listening
                  </Button>
                ) : (
                  <Button
                    variant="outlined"
                    onClick={handleStartVoice}
                    startIcon={<KeyboardVoiceIcon />}
                    disabled={loading}
                  >
                    Use Voice
                  </Button>
                )}
                <Typography variant="body2" color="text.secondary">
                  {speechStatus === SPEECH_LISTENING && 'Listening...'}
                  {speechStatus === SPEECH_PROCESSING && 'Processing speech...'}
                  {speechStatus === SPEECH_IDLE && 'Voice ready'}
                  {speechStatus === SPEECH_ERROR && 'Voice error'}
                  {autoSubmitPending && ' - auto submitting...'}
                </Typography>
                {lastRequestUsedVoice && !showAnswerText && (
                  <Button size="small" onClick={() => setShowAnswerText(true)}>
                    Show text
                  </Button>
                )}
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Voice input is not supported in this browser; type your question instead.
              </Typography>
            )}
          </Box>

          {speechError && <Alert severity="warning">{speechError}</Alert>}
          {error && <Alert severity="error">{error}</Alert>}
          {ttsNotice && <Alert severity="info">{ttsNotice}</Alert>}

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              type="submit"
              variant="contained"
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
              disabled={loading || !question.trim()}
            >
              {loading ? 'Asking...' : 'Ask Ranch Assistant'}
            </Button>
            <Button
              type="button"
              variant="text"
              onClick={() => {
                clearAutoSubmitTimer();
                pendingAutoSubmitRef.current = false;
                latestTranscriptRef.current = '';
                latestQuestionDraftRef.current = '';
                setQuestion('');
                setAnswer('');
                setSql('');
                setRowCount(0);
                setError('');
                setSpeechError('');
                stopSpeaking();
              }}
              disabled={loading}
            >
              Clear
            </Button>
          </Box>
          {ttsSupported && (
            <FormControlLabel
              control={
                <Switch
                  checked={ttsEnabled}
                  onChange={(event) => setTtsEnabled(event.target.checked)}
                  disabled={!selectedVoiceIdRef.current || !voicesLoaded}
                />
              }
              label="Auto-read answers"
            />
          )}
        </Stack>
      </Paper>

      {(answer || sql) && (!lastRequestUsedVoice || showAnswerText) && (
        <Paper sx={{ ...cardSx, p: 3, mt: 2, borderRadius: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
            Answer
          </Typography>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
            {answer || 'No response returned.'}
          </Typography>
          {ttsSupported && (
            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              {selectedVoice && (
                <Typography variant="caption" color="text.secondary">
                  Voice: {selectedVoice}
                </Typography>
              )}
              {isSpeaking ? (
                <Button size="small" startIcon={<VolumeOffIcon />} onClick={stopSpeaking} disabled={!answer.trim()}>
                  Stop Audio
                </Button>
              ) : (
                <Button size="small" startIcon={<VolumeUpIcon />} onClick={() => speakText(answer)} disabled={!answer.trim() || !selectedVoiceIdRef.current}>
                  Speak
                </Button>
              )}
            </Box>
          )}

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Rows returned: {rowCount}
          </Typography>

          {!!sql && (
            <>
              <Box sx={{ mt: 2 }}>
                <Button
                  size="small"
                  startIcon={<CodeIcon />}
                  onClick={() => setShowSql((prev) => !prev)}
                >
                  {showSql ? 'Hide SQL' : 'Show SQL'}
                </Button>
              </Box>
              <Collapse in={showSql}>
                <Paper variant="outlined" sx={{ p: 1.5, mt: 1, backgroundColor: 'grey.100' }}>
                  <Typography variant="body2" component="pre" sx={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                    {sql}
                  </Typography>
                </Paper>
              </Collapse>
            </>
          )}
        </Paper>
      )}
    </Box>
  );
}

export default RanchAssistantPage;
